import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { ContentContainer, GeneralContainer, StyledTextField, SubTitle, Title, TitleContainer } from '../../common';
import { New } from '../../common/banners/new';
import { Instructions } from './Instructions';
import { InstructionsTooltip } from './InstructionsTooltip';

const DEFAULT_PERSONALITY = 'Friendly and Professional';
const PERSONALITY_OPTIONS = [
    DEFAULT_PERSONALITY,
    'Fun and Engaging',
    'Warm and Supportive',
    'Direct and Concise',
    'Sophisticated and Formal',
    'Confident and Persuasive',
];

const FieldLabel = styled.label`
    display: flex;
    align-items: center;

    gap: 2px;

    margin-bottom: 4px;

    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    letter-spacing: -0.28px;
    line-height: 20px;

    color: ${theme.colors.secondary.main};
`;

const OpaqueFieldLabel = styled(FieldLabel.withComponent('span'))`
    margin: unset;

    color: ${theme.colors.secondary[500]};
    font-weight: 400;
`;

const PERSONALITY_FONT_SIZE = '12px';
const StyledAdornmentSelect = styled(AdornmentSelect<string, true>)`
    & .MuiInputBase-root {
        input {
            padding: 0 !important;
            font-size: ${PERSONALITY_FONT_SIZE};
        }
    }

    & .MuiAutocomplete-inputRoot {
        padding: 10px;
        padding-left: 16px;
    }
`;

export const AgentBehaviour = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const onPersonalityChange = useCallback((_event: React.SyntheticEvent, value: string | null) => {
        agentEditorStore.setLLM(prev => ({
            ...prev,
            prompt_customization: { ...prev.prompt_customization, personality: value ?? undefined },
        }));
    }, []);

    return (
        <ContentContainer>
            <TitleContainer>
                <Title>Set your agent behavior</Title>
                <SubTitle>Customize your agent's role</SubTitle>
            </TitleContainer>

            <GeneralContainer>
                <FieldLabel>
                    What's your agent role <OpaqueFieldLabel>(Optional)</OpaqueFieldLabel> <New />
                </FieldLabel>
                <StyledTextField
                    placeholder="e.g Customer success manager"
                    value={agentEditorStore.llm.prompt_customization?.role}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ maxLength: 250 }}
                    onChange={e =>
                        agentEditorStore.setLLM(llm => ({
                            ...llm,
                            prompt_customization: { ...llm.prompt_customization, role: e.target.value },
                        }))
                    }
                />
            </GeneralContainer>

            <GeneralContainer>
                <FieldLabel>
                    Add instructions
                    <OpaqueFieldLabel>(Optional)</OpaqueFieldLabel>
                    <InstructionsTooltip />
                </FieldLabel>

                <Instructions />
            </GeneralContainer>

            <GeneralContainer>
                <FieldLabel>
                    Personality <New />
                </FieldLabel>
                <StyledAdornmentSelect
                    freeSolo
                    forcePopupIcon
                    id="personality-select"
                    options={PERSONALITY_OPTIONS}
                    value={agentEditorStore.llm.prompt_customization?.personality ?? DEFAULT_PERSONALITY}
                    onInputChange={onPersonalityChange}
                    onChange={onPersonalityChange}
                    getOptionLabel={option => option}
                    optionSx={{ fontSize: PERSONALITY_FONT_SIZE }}
                />
            </GeneralContainer>
        </ContentContainer>
    );
});
